import React, { useState, useEffect } from 'react';
import { auditLogApi, auditAnalyticsApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Shield, Search, Download, ChevronLeft, ChevronRight, Filter,
  User, Activity, Clock, MapPin, RefreshCw, BarChart3, TrendingUp
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend } from 'recharts';
import { toast } from 'sonner';

const CHART_COLORS = ['#0E7490', '#1A56DB', '#166534', '#92400E', '#991B1B', '#7C3AED', '#DB2777', '#0A2342'];

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [filters, setFilters] = useState({
    action: '', resource_type: '', start_date: '', end_date: '', user_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => { loadLogs(); loadActions(); loadAnalytics(); }, [page]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 30 };
      if (filters.action) params.action = filters.action;
      if (filters.resource_type) params.resource_type = filters.resource_type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.user_id) params.user_id = filters.user_id;
      
      const res = await auditLogApi.list(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadActions = async () => {
    try {
      const res = await auditLogApi.getActions();
      setActions(res.data.actions || []);
      setResourceTypes(res.data.resource_types || []);
    } catch (err) { /* skip */ }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await auditAnalyticsApi.get();
      setAnalytics(res.data);
    } catch (err) { /* skip */ }
    finally { setAnalyticsLoading(false); }
  };

  const applyFilters = () => {
    setPage(1);
    loadLogs();
  };

  const clearFilters = () => {
    setFilters({ action: '', resource_type: '', start_date: '', end_date: '', user_id: '' });
    setPage(1);
    setTimeout(loadLogs, 0);
  };

  const exportLogs = () => {
    const csv = ['Timestamp,User,Action,Resource Type,Resource ID,IP Address']
      .concat(logs.map(l => `${l.timestamp},${l.user_name},${l.action},${l.resource_type},${l.resource_id},${l.ip_address}`))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'audit_logs.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit logs exported');
  };

  const getActionColor = (action) => {
    if (action?.includes('login')) return 'bg-[#0E7490]/10 text-[#0E7490]';
    if (action?.includes('create') || action?.includes('register')) return 'bg-[#166534]/10 text-[#166534]';
    if (action?.includes('delete') || action?.includes('failed')) return 'bg-[#991B1B]/10 text-[#991B1B]';
    if (action?.includes('update') || action?.includes('edit')) return 'bg-[#1A56DB]/10 text-[#1A56DB]';
    if (action?.includes('ai_')) return 'bg-[#7C3AED]/10 text-[#7C3AED]';
    return 'bg-[#F1F5F9] text-[#64748B]';
  };

  const formatTime = (ts) => {
    if (!ts) return 'Unknown';
    try {
      return new Date(ts).toLocaleString();
    } catch { return ts; }
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Audit Log Viewer
          </h1>
          <p className="text-[#64748B] mt-1">
            {total.toLocaleString()} total events tracked
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters">
            <Filter className="w-4 h-4 mr-2" />
            Filters {showFilters ? '(hide)' : ''}
          </Button>
          <Button variant="outline" onClick={exportLogs} data-testid="export-logs">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => { setPage(1); loadLogs(); }} data-testid="refresh-logs">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Analytics Charts */}
      {analytics && (
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="careiq-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#64748B]">Activity (24h)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={analytics.hourly_activity}>
                  <XAxis dataKey="hour" fontSize={9} stroke="#94A3B8" interval={3} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#0E7490" fill="#0E7490" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="careiq-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#64748B]">Top Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={analytics.top_actions.slice(0, 6)} layout="vertical">
                  <XAxis type="number" fontSize={9} stroke="#94A3B8" />
                  <YAxis type="category" dataKey="action" fontSize={8} stroke="#94A3B8" width={60} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0E7490" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="careiq-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#64748B]">Resource Types</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={120}>
                <PieChart>
                  <Pie data={analytics.resource_breakdown} cx="50%" cy="50%" outerRadius={45} dataKey="count" nameKey="resource">
                    {analytics.resource_breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <Card className="careiq-card">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div>
                <label className="text-xs text-[#64748B]">Action</label>
                <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm" data-testid="filter-action">
                  <option value="">All Actions</option>
                  {actions.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Resource Type</label>
                <select value={filters.resource_type} onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })} className="w-full mt-1 rounded-md border border-[#E2E8F0] p-2 text-sm" data-testid="filter-resource">
                  <option value="">All Types</option>
                  {resourceTypes.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#64748B]">Start Date</label>
                <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="mt-1" />
              </div>
              <div>
                <label className="text-xs text-[#64748B]">End Date</label>
                <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="mt-1" />
              </div>
              <div className="flex items-end gap-2">
                <Button size="sm" className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={applyFilters} data-testid="apply-filters">
                  <Search className="w-3 h-3 mr-1" /> Apply
                </Button>
                <Button size="sm" variant="outline" onClick={clearFilters} data-testid="clear-filters">
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Log Table */}
      <Card className="careiq-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <th className="text-left p-3 text-[#64748B] font-medium">Timestamp</th>
                    <th className="text-left p-3 text-[#64748B] font-medium">User</th>
                    <th className="text-left p-3 text-[#64748B] font-medium">Action</th>
                    <th className="text-left p-3 text-[#64748B] font-medium">Resource</th>
                    <th className="text-left p-3 text-[#64748B] font-medium">IP Address</th>
                    <th className="text-left p-3 text-[#64748B] font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={log.id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`audit-log-${i}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-1 text-xs text-[#64748B]">
                          <Clock className="w-3 h-3" />
                          {formatTime(log.timestamp)}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3 text-[#64748B]" />
                          <span className="text-[#0A2342] font-medium text-xs">{log.user_name}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="text-xs">
                          <span className="text-[#0A2342] font-medium">{log.resource_type}</span>
                          {log.resource_id && log.resource_id !== 'na' && (
                            <span className="text-[#94A3B8] ml-1">#{log.resource_id.substring(0, 8)}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 text-xs text-[#64748B]">
                          <MapPin className="w-3 h-3" />
                          {log.ip_address}
                        </div>
                      </td>
                      <td className="p-3">
                        {log.details && Object.keys(log.details).length > 0 && (
                          <details>
                            <summary className="text-xs text-[#0E7490] cursor-pointer">View</summary>
                            <pre className="text-[10px] text-[#64748B] mt-1 max-w-xs overflow-auto">{JSON.stringify(log.details, null, 1)}</pre>
                          </details>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 text-[#64748B]">
              <Shield className="w-12 h-12 mx-auto mb-2 text-[#94A3B8]" />
              <p>No audit logs found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#64748B]">
            Page {page} of {totalPages} ({total} total)
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
